import { useRef, useState, type ComponentPropsWithRef } from 'react';

import { Icon } from '@/components/ui/icon';
import { CONTROL_BASE } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export interface NumberInputProps extends Omit<
  ComponentPropsWithRef<'input'>,
  'type' | 'value' | 'onChange' | 'min' | 'max'
> {
  /** Empty string means "no value", which a bare `number` cannot express. */
  value: number | '';
  onChange: (value: number | '') => void;
  min?: number;
  max?: number;
  step?: number;
  invalid?: boolean;
  /** Shows the custom +/- steppers. */
  steppers?: boolean;
  /**
   * How many decimal places to accept. 0 - the default - keeps the field a
   * whole-number one, which is what every count in the product needs.
   *
   * Money fields pass 2. They have to: this control used to strip the decimal
   * point unconditionally, so a salary typed as 6543.21 was silently stored as
   * 654321, a hundred times too much.
   */
  decimalPlaces?: number;
  /** Whether a leading minus is accepted - an overdrawn opening balance, a
      deduction entered as a negative adjustment. */
  allowNegative?: boolean;
}

/**
 * An integer field that behaves predictably.
 *
 * `<input type="number">` is a poor primitive on its own: it accepts `e`, `+`
 * and `-` anywhere in the string, silently reports an empty value for anything
 * it cannot parse, and changes the value when the wheel is scrolled over a
 * focused field - which quietly corrupts a form the user was only scrolling
 * past. This keeps the numeric keypad on mobile via `inputMode` while taking
 * over parsing, clamping and stepping.
 */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  invalid = false,
  steppers = true,
  decimalPlaces = 0,
  allowNegative = false,
  disabled,
  className,
  ...props
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * What is actually in the box while somebody is typing.
   *
   * Needed because a number cannot represent a half-written one: "12." and
   * "-" are both states a user passes through, and echoing the parsed value
   * back would delete the character they just typed. Null means "not being
   * edited", and the displayed text comes from `value` instead.
   */
  const [draft, setDraft] = useState<string | null>(null);

  function clamp(next: number): number {
    if (min !== undefined && next < min) {
      return min;
    }
    if (max !== undefined && next > max) {
      return max;
    }
    return next;
  }

  /**
   * Strips everything the field does not accept, without fighting the typist.
   *
   * No exponent and no stray signs - the two things `<input type="number">`
   * lets through and then reports as empty. A minus is kept only at the front,
   * and only where negatives are allowed; decimals are capped at the permitted
   * number of places rather than rejected, so pasting an over-precise figure
   * truncates instead of refusing.
   */
  function sanitise(raw: string): string {
    const negative = allowNegative && raw.trimStart().startsWith('-');
    let body = raw.replace(/[^\d.]/g, '');

    if (decimalPlaces <= 0) {
      body = body.replace(/\./g, '');
    } else {
      const [whole = '', ...rest] = body.split('.');
      body = rest.length > 0 ? `${whole}.${rest.join('').slice(0, decimalPlaces)}` : whole;
    }

    return negative ? `-${body}` : body;
  }

  function handleChange(raw: string): void {
    const cleaned = sanitise(raw);
    setDraft(cleaned);

    if (cleaned === '') {
      onChange('');
      return;
    }

    /* "12." and "-" are on the way to a number, not numbers. Holding the draft
       and emitting nothing keeps the caret where the user put it. */
    if (cleaned === '-' || cleaned.endsWith('.')) {
      return;
    }

    const parsed = Number(cleaned);

    if (Number.isFinite(parsed)) {
      onChange(parsed);
    }
  }

  /** Clamping happens on blur, so typing "1" on the way to "15" is not fought. */
  function handleBlur(): void {
    /* Editing is over, so the box goes back to showing the value itself -
       which also tidies "007" and a trailing point away. */
    setDraft(null);

    if (value === '') {
      return;
    }
    const clamped = clamp(value);
    if (clamped !== value) {
      onChange(clamped);
    }
  }

  function nudge(delta: number): void {
    const base = value === '' ? (min ?? 0) : value;

    /* Rounded to the field's own precision: stepping 0.01 repeatedly in
       binary floating point otherwise lands on 0.30000000000000004. */
    onChange(clamp(Number((base + delta).toFixed(Math.max(0, decimalPlaces)))));
    setDraft(null);
    inputRef.current?.focus();
  }

  const atMin = value !== '' && min !== undefined && value <= min;
  const atMax = value !== '' && max !== undefined && value >= max;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        /* `text` with a numeric inputMode, not `type="number"`: it keeps the
           phone keypad but drops the wheel-scroll and exponent behaviour. */
        type="text"
        inputMode={decimalPlaces > 0 ? 'decimal' : 'numeric'}
        autoComplete="off"
        value={draft ?? (value === '' ? '' : String(value))}
        disabled={disabled}
        aria-invalid={invalid ? true : undefined}
        role="spinbutton"
        aria-valuenow={value === '' ? undefined : value}
        aria-valuemin={min}
        aria-valuemax={max}
        onChange={(event) => {
          handleChange(event.target.value);
        }}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          /* Arrow keys step the value, as they would on a native spinbutton. */
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            nudge(step);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            nudge(-step);
          }
        }}
        className={cn(
          CONTROL_BASE,
          'h-9 px-2 text-xs tabular-nums sm:h-7',
          steppers && 'pr-7',
          invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
          className,
        )}
        {...props}
      />

      {steppers && !disabled && (
        <div className="absolute top-1/2 right-1 flex -translate-y-1/2 flex-col">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increase"
            disabled={atMax}
            onClick={() => {
              nudge(step);
            }}
            className="-mb-0.5 rounded-xs px-0.5 text-content-subtle hover:text-content disabled:opacity-30"
          >
            <Icon name="chevron-up" size={11} />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrease"
            disabled={atMin}
            onClick={() => {
              nudge(-step);
            }}
            className="rounded-xs px-0.5 text-content-subtle hover:text-content disabled:opacity-30"
          >
            <Icon name="chevron-down" size={11} />
          </button>
        </div>
      )}
    </div>
  );
}
